import { DetailedSensor } from '@app/app/sensors/DetailedSensor';
import { CommLink, Degrees, Kilometers, Milliseconds, SpaceObjectType, ZoomValue } from '@ootk/src/main';
import { Operators, SensorList } from './types';

/**
 * Galileo ground segment TT&C stations (EUSPA). S-band dishes that command
 * and track the Galileo MEO constellation (23,222 km circular orbits).
 * Several sites are co-located with ESA/CNES ground stations.
 *
 * https://www.euspa.europa.eu/eu-space-programme/galileo
 */
export const galileoSensors = <SensorList>{
  GALKIR: new DetailedSensor({
    objName: 'GALKIR',
    shortName: 'GKIR',
    id: 0,
    name: 'Galileo TT&C Station, Kiruna, Sweden',
    uiName: 'Kiruna TT&C',
    system: 'Galileo TT&C Network',
    freqBand: 'S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>67.858, // Co-located with ESTRACK Kiruna
    lon: <Degrees>20.967,
    alt: <Kilometers>0.402,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>30000, // Galileo MEO slant range plus margin
    beamwidth: <Degrees>0.7, // 13m dish at S-band
    commLinks: [CommLink.GALILEO],
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.euspa.europa.eu/eu-space-programme/galileo',
    country: 'Sweden',
    operator: Operators.EUSPA,
  }),
  GALKRU: new DetailedSensor({
    objName: 'GALKRU',
    shortName: 'GKRU',
    id: 0,
    name: 'Galileo TT&C Station, Kourou, French Guiana',
    uiName: 'Kourou TT&C',
    system: 'Galileo TT&C Network',
    freqBand: 'S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>5.2515, // Co-located with ESTRACK Kourou
    lon: <Degrees>-52.8047,
    alt: <Kilometers>0.014,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>30000,
    beamwidth: <Degrees>0.7,
    commLinks: [CommLink.GALILEO],
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.euspa.europa.eu/eu-space-programme/galileo',
    country: 'France',
    operator: Operators.EUSPA,
  }),
  GALNOU: new DetailedSensor({
    objName: 'GALNOU',
    shortName: 'GNOU',
    id: 0,
    name: 'Galileo TT&C Station, Noumea, New Caledonia',
    uiName: 'Noumea TT&C',
    system: 'Galileo TT&C Network',
    freqBand: 'S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>-22.2758, // Approximate
    lon: <Degrees>166.458,
    alt: <Kilometers>0.01,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>30000,
    beamwidth: <Degrees>0.7,
    commLinks: [CommLink.GALILEO],
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.euspa.europa.eu/eu-space-programme/galileo',
    country: 'France',
    operator: Operators.EUSPA,
  }),
  GALPAP: new DetailedSensor({
    objName: 'GALPAP',
    shortName: 'GPAP',
    id: 0,
    name: 'Galileo TT&C Station, Papeete, Tahiti, French Polynesia',
    uiName: 'Papeete TT&C',
    system: 'Galileo TT&C Network',
    freqBand: 'S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>-17.5769, // Approximate; co-located with CNES Tahiti station
    lon: <Degrees>-149.6062,
    alt: <Kilometers>0.09,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>30000,
    beamwidth: <Degrees>0.7,
    commLinks: [CommLink.GALILEO],
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.euspa.europa.eu/eu-space-programme/galileo',
    country: 'France',
    operator: Operators.EUSPA,
  }),
  GALREU: new DetailedSensor({
    objName: 'GALREU',
    shortName: 'GREU',
    id: 0,
    name: 'Galileo TT&C Station, Sainte-Marie, Reunion',
    uiName: 'Reunion TT&C',
    system: 'Galileo TT&C Network',
    freqBand: 'S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>-20.8907, // Approximate
    lon: <Degrees>55.5157,
    alt: <Kilometers>0.06,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>30000,
    beamwidth: <Degrees>0.7,
    commLinks: [CommLink.GALILEO],
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.euspa.europa.eu/eu-space-programme/galileo',
    country: 'France',
    operator: Operators.EUSPA,
  }),
  GALRED: new DetailedSensor({
    objName: 'GALRED',
    shortName: 'GRED',
    id: 0,
    name: 'Galileo In-Orbit Test Station, Redu, Belgium',
    uiName: 'Redu IOT',
    system: 'Galileo In-Orbit Test',
    freqBand: 'L/S-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>50.0019, // Co-located with ESTRACK Redu
    lon: <Degrees>5.1457,
    alt: <Kilometers>0.386,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>30000,
    beamwidth: <Degrees>0.5, // 20m IOT dish; approximate
    commLinks: [CommLink.GALILEO],
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.euspa.europa.eu/eu-space-programme/galileo',
    country: 'Belgium',
    operator: Operators.EUSPA,
  }),
};
